/**
 * Resolve UI anchors for Theme Creator decorations (responsive % placement).
 * Geometric matching works even when targets use pointer-events: none (e.g. home title).
 */

export type ThemeAttachHit = { el: HTMLElement; selector: string };

function distPointToRect(x: number, y: number, r: DOMRect): number {
  const dx = Math.max(r.left - x, 0, x - r.right);
  const dy = Math.max(r.top - y, 0, y - r.bottom);
  return Math.hypot(dx, dy);
}

function isUsableTarget(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  if (el.classList.contains("hidden")) return false;
  if (el.getAttribute("aria-hidden") === "true" && !el.classList.contains("homeTitle")) return false;
  const r = el.getBoundingClientRect();
  return r.width >= 4 && r.height >= 4;
}

function pushCandidate(
  out: ThemeAttachHit[],
  seen: Set<string>,
  el: HTMLElement | null | undefined,
  selector: string,
): void {
  if (!el || !isUsableTarget(el)) return;
  if (seen.has(selector)) return;
  seen.add(selector);
  out.push({ el, selector });
}

/** Collect stable attach targets currently in the DOM. */
export function listThemeAttachCandidates(): ThemeAttachHit[] {
  if (typeof document === "undefined") return [];
  const out: ThemeAttachHit[] = [];
  const seen = new Set<string>();

  document.querySelectorAll<HTMLElement>("[data-theme-anchor]").forEach((el) => {
    const id = el.getAttribute("data-theme-anchor");
    if (!id) return;
    pushCandidate(out, seen, el, `[data-theme-anchor="${CSS.escape(id)}"]`);
  });

  document.querySelectorAll<HTMLElement>("[data-home-widget]").forEach((el) => {
    const id = el.getAttribute("data-home-widget");
    if (!id) return;
    pushCandidate(out, seen, el, `[data-home-widget="${CSS.escape(id)}"]`);
  });

  document.querySelectorAll<HTMLElement>("#gameHubGrid [data-hub-category]").forEach((el) => {
    const cat = el.getAttribute("data-hub-category");
    if (!cat) return;
    pushCandidate(out, seen, el, `#gameHubGrid [data-hub-category="${CSS.escape(cat)}"]`);
  });

  document.querySelectorAll<HTMLElement>("#sideNav .sideNavBtn[id]").forEach((el) => {
    if (!el.id) return;
    pushCandidate(out, seen, el, `#${CSS.escape(el.id)}`);
  });

  document.querySelectorAll<HTMLElement>("#modesContainer .modeBox[id], #modeScreen .modeBox[id]").forEach((el) => {
    if (!el.id) return;
    pushCandidate(out, seen, el, `#${CSS.escape(el.id)}`);
  });

  pushCandidate(out, seen, document.getElementById("openModes"), "#openModes");
  pushCandidate(
    out,
    seen,
    document.querySelector("#startScreen .homeTitle") as HTMLElement | null,
    "#startScreen .homeTitle",
  );
  pushCandidate(
    out,
    seen,
    document.querySelector("#startScreen .homeTitleWrap") as HTMLElement | null,
    "#startScreen .homeTitleWrap",
  );

  return out;
}

/**
 * Resolve a stable CSS selector for sticking a decoration to a UI widget under the pointer.
 * Returns null for free-floating viewport-% placement.
 */
export function resolveThemeAttachTarget(clientX: number, clientY: number): ThemeAttachHit | null {
  if (typeof document === "undefined") return null;

  const stack = document.elementsFromPoint(clientX, clientY);
  for (const node of stack) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.closest("#themePreviewChrome")) continue;
    if (node.closest(".themePreviewDeviceFrame")) continue;
    if (node.closest(".themeDrawImportModal")) continue;
    if (node.closest(".themeDrawOverlay")) continue;
    if (node.closest(".gameThemeDecorLayer")) continue;
    if (node.closest(".gameThemeBgDecorLayer")) continue;
    if (node.closest(".themePlaceGhost")) continue;
    if (node.closest(".gameThemeDecor--inHost")) continue;

    const anchor = node.closest<HTMLElement>("[data-theme-anchor]");
    if (anchor) {
      const id = anchor.getAttribute("data-theme-anchor");
      if (id) return { el: anchor, selector: `[data-theme-anchor="${CSS.escape(id)}"]` };
    }

    const hub = node.closest<HTMLElement>("[data-hub-category]");
    if (hub) {
      const cat = hub.getAttribute("data-hub-category");
      if (cat) {
        return { el: hub, selector: `#gameHubGrid [data-hub-category="${CSS.escape(cat)}"]` };
      }
    }

    const widget = node.closest<HTMLElement>("[data-home-widget]");
    if (widget) {
      const id = widget.getAttribute("data-home-widget");
      if (id) return { el: widget, selector: `[data-home-widget="${CSS.escape(id)}"]` };
    }

    if (node.closest("#openModes") || node.closest(".homeStartBtn")) {
      const startBtn = document.getElementById("openModes");
      if (startBtn) return { el: startBtn, selector: "#openModes" };
    }

    const sideBtn = node.closest<HTMLElement>("#sideNav .sideNavBtn[id]");
    if (sideBtn?.id) return { el: sideBtn, selector: `#${CSS.escape(sideBtn.id)}` };

    const modeCard = node.closest<HTMLElement>("#modesContainer .modeBox[id], #modeScreen .modeBox[id]");
    if (modeCard?.id) return { el: modeCard, selector: `#${CSS.escape(modeCard.id)}` };
  }

  // Geometric nearest — required for pointer-events:none targets (home title).
  const candidates = listThemeAttachCandidates();
  let best: ThemeAttachHit | null = null;
  let bestDist = Infinity;
  for (const c of candidates) {
    const r = c.el.getBoundingClientRect();
    const d = distPointToRect(clientX, clientY, r);
    const threshold = Math.max(40, Math.min(r.width, r.height) * 0.55, Math.max(r.width, r.height) * 0.22);
    if (d <= threshold && d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return best;
}

/** Size a decoration as % of an anchor box (responsive across resolutions). */
export function themeDecorSizePctForBox(
  box: { width: number; height: number },
  preferredCssPx = 96,
): { widthPct: number; heightPct: number } {
  const ref = Math.max(1, Math.min(box.width, box.height));
  const targetPx = Math.max(24, Math.min(preferredCssPx, ref * 0.42));
  return {
    widthPct: Math.max(4, Math.min(90, (targetPx / box.width) * 100)),
    heightPct: Math.max(4, Math.min(90, (targetPx / box.height) * 100)),
  };
}

/** Size a free-floating decoration as % of the viewport. */
export function themeDecorSizePctForViewport(
  vw: number,
  vh: number,
  preferredCssPx = 96,
): { widthPct: number; heightPct: number } {
  const ref = Math.max(1, Math.min(vw, vh));
  const targetPx = Math.max(28, Math.min(preferredCssPx, ref * 0.14));
  return {
    widthPct: Math.max(3, Math.min(40, (targetPx / Math.max(1, vw)) * 100)),
    heightPct: Math.max(3, Math.min(40, (targetPx / Math.max(1, vh)) * 100)),
  };
}
